import queryString from 'query-string'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ReactRouterPropTypes from 'react-router-prop-types'

import { Breadcrumb } from 'legacy/components/Breadcrumb/Breadcrumb'
import { LogoLoading } from 'legacy/components/LogoLoading/LogoLoading'
import { CONTEXTS } from 'legacy/containers/LoadsPage/OfferCard/OfferCard'
import { LoadBoardContext } from 'legacy/contexts/loadBoard'
import { getLoad, getPublicLoad } from 'legacy/services/loads'
import { analytics as analyticsUtils } from 'legacy/utils/analytics'
import { sentry } from 'legacy/utils/sentry'
import { user } from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'
import { RelatedLoadsList } from 'loads/ui/related-load/RelatedLoadsList'
import { createPreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { PreferredLaneToggle } from 'preferred-lanes/ui/PreferredLaneToggle'

import { ShipperMarket } from '../constants/LoadOffer'
import { useAnalytics } from '../hooks/useAnalytics'
import { LoadTaken } from '../LoadTaken/LoadTaken'
import { OutletDialog } from '../OutletDialog/OutletDialog'
import { RejectLoadModal } from '../RejectLoadModal/RejectLoadModal'
import * as laneUtils from '../utils/laneUtils'
import * as loadUtils from '../utils/loadUtils'
import { ActionsFooter } from './ActionsFooter/ActionsFooter'
import {
  FooterContainer,
  HeaderContainer,
  LoadDetailsPageContainer,
  LoadInformationContainer,
  MainContentContainer,
  MapWrapper,
  RelatedLoadsWrapper,
} from './LoadDetails.styles'
import { LoadDetailsBanner } from './LoadDetailsBanner'
import { LoadInformation } from './LoadInformation/LoadInformation'
import { StopsMap } from './StopsMap/StopsMap'

function LoadDetails(props) {
  const { match, location } = props

  /* router */
  const { params } = match
  const { loadUUID } = params
  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search])
  const fromDATRef = useRef((parsedQuery.utm_source || '').toLowerCase() === 'dat')

  /* context data */
  const userIsLoggedIn = useMemo(() => user.isLoggedIn(), [])
  const [{ loadUUIDToRefreshAfterBid }] = useContext(LoadBoardContext)

  /* state */
  const [load, setLoad] = useState()
  const [isLoadingOffer, setLoadingLoad] = useState(loadUUID !== load?.uuid)
  const [rejectModalIsOpen, setRejectModalOpen] = useState(false)

  /* utils */
  const { analytics } = useAnalytics()

  const isLoadTaken = loadUtils.isLoadTaken(load)
  const isLoadOfferAvailable = loadUtils.isLoadOfferAvailable(load, isLoadTaken)
  const lane = useMemo(() => load && createPreferredLane(load.stops, load.equipment_type), [load])
  const laneDescription = lane && laneUtils.getPreferredLaneDescription(lane)
  const showPreferredLane = laneUtils.showPreferredLane(userIsLoggedIn, isLoadTaken, lane)

  const backToLoadboardURL = `/loads/${location.search}`
  const showOutletDialog = fromDATRef.current && !userIsLoggedIn
  const showOnboardingImprovements = useConfigContext().showOnboardingImprovements.toBoolean()
  const shouldRefreshBecauseOfBid = Boolean(loadUUIDToRefreshAfterBid === loadUUID)
  const relatedLoadOfferContext = userIsLoggedIn
    ? CONTEXTS.RELATED_LOAD
    : CONTEXTS.PUBLIC_RELATED_LOAD

  /* data fetching */
  const fetchOffer = useCallback(async () => {
    setLoadingLoad(true)
    try {
      let fetchLoadFn

      if (userIsLoggedIn) {
        fetchLoadFn = getLoad
      } else {
        fetchLoadFn = getPublicLoad
      }

      const response = await fetchLoadFn(loadUUID)
      const loadOfferData = response.data

      setLoad(loadOfferData)
      window.scrollTo(0, 0)
    } catch (error) {
      if (!error.response?.status) {
        sentry.log(error)
      }
      analytics.track(['Load Details', 'Fetch Error'], {
        description: error.response?.data?.detail,
        status: error.response?.status,
      })

      analytics.tag(['Load Details Page Error'])
    } finally {
      setLoadingLoad(false)
    }
  }, [userIsLoggedIn, loadUUID, analytics])

  /* handlers and callbacks */
  function handleRejectModalClose() {
    return setRejectModalOpen(false)
  }

  /* effects */
  useEffect(() => {
    if (loadUUID || shouldRefreshBecauseOfBid) {
      fetchOffer(loadUUID)
    }
  }, [fetchOffer, loadUUID, shouldRefreshBecauseOfBid])

  useEffect(() => {
    if (loadUUID) {
      const eventPayload = { loadUUID }
      if (userIsLoggedIn) analytics.track(['Load Details', 'View'], eventPayload)
      else analytics.track(['Load Details', 'Public', 'View'], eventPayload)
    }
  }, [analytics, loadUUID, userIsLoggedIn])

  useEffect(() => {
    if (loadUUID && !isLoadingOffer) {
      const shipperMarket = load?.shipper_market

      if (shipperMarket === ShipperMarket.Contracted) {
        analyticsUtils.trigger('contracted-load')
      }

      analytics.track(['Load Details', 'Load Information'], {
        loadUUID,
        lane: laneDescription,
        loadTaken: isLoadTaken,
        shipperMarket,
      })
    }
  }, [loadUUID, isLoadTaken, isLoadingOffer, analytics, laneDescription, load])

  useEffect(() => {
    if (loadUUID && !isLoadingOffer && isLoadTaken) {
      const eventPayload = { loadUUID, lane: laneDescription }
      if (userIsLoggedIn) analytics.track(['Load Details', 'Offer Taken'], eventPayload)
      else analytics.track(['Load Details', 'Public', 'Offer Taken'], eventPayload)
    }
  }, [loadUUID, userIsLoggedIn, isLoadTaken, isLoadingOffer, analytics, laneDescription])

  useEffect(() => {
    if (fromDATRef.current) {
      if (userIsLoggedIn) {
        analytics.tag(['Load Details / DAT'])
      } else {
        analytics.tag(['Load Details / Public / DAT'])
      }
    }
  }, [analytics, userIsLoggedIn])

  useEffect(() => {
    if (parsedQuery.utm_campaign === 'supply_led') {
      analytics.track(['Load Details', 'Fake Load'], {
        interested: parsedQuery.interested,
      })
    }
  }, [analytics, parsedQuery.utm_campaign, parsedQuery.interested])

  /* render */

  if (!loadUUID) {
    return null
  }

  return (
    <LoadDetailsPageContainer>
      <HeaderContainer>
        {userIsLoggedIn && (
          <Breadcrumb
            path={[
              {
                label: 'Loads',
                url: backToLoadboardURL,
              },
              { label: 'Load Details' },
            ]}
          />
        )}

        {showPreferredLane && (
          <PreferredLaneToggle preferredLane={lane} analyticsPrefix="Load Details" />
        )}
      </HeaderContainer>

      <LoadDetailsBanner
        userIsLoggedIn={userIsLoggedIn}
        showOnboardingImprovements={showOnboardingImprovements}
      />

      {isLoadingOffer ? (
        <LogoLoading />
      ) : (
        <>
          <MainContentContainer hasMarginTop={!userIsLoggedIn}>
            {isLoadOfferAvailable ? (
              <>
                <LoadInformationContainer>
                  <LoadInformation load={load} />
                  <FooterContainer>
                    <ActionsFooter
                      load={load}
                      isLoading={isLoadingOffer}
                      setRejectModalOpen={setRejectModalOpen}
                    />
                  </FooterContainer>
                </LoadInformationContainer>
                <MapWrapper>
                  <StopsMap load={load} isLoading={isLoadingOffer} />
                </MapWrapper>
              </>
            ) : (
              <LoadTaken lane={lane} userIsLoggedIn={userIsLoggedIn} />
            )}
          </MainContentContainer>
          <RelatedLoadsWrapper>
            <RelatedLoadsList context={relatedLoadOfferContext} loadUUID={loadUUID} />
          </RelatedLoadsWrapper>
        </>
      )}

      {load && !isLoadingOffer && (
        <RejectLoadModal load={load} isOpen={rejectModalIsOpen} onClose={handleRejectModalClose} />
      )}

      {!isLoadingOffer && showOutletDialog && <OutletDialog load={load} lane={lane} />}
    </LoadDetailsPageContainer>
  )
}

LoadDetails.propTypes = {
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
}

export { LoadDetails }
